"use client"

import { percentageFormatter } from "@/app/percentage/utils"
import { PriceInput } from "@/app/price/price-input"
import type { Currency } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import { Button } from "@rarible_int/anaconda_ui_components"
import Big from "big.js"
import { useEffect, useMemo, useState } from "react"
import { useCollectionStatistic } from "../store/use-collection"

interface Props {
  initialPrice?: number
  onPriceChange(price: number | undefined): void
  error?: React.ReactNode
  topOffer?: number
  currency: Currency
}

export function OfferPriceInput({
  initialPrice,
  onPriceChange,
  error,
  currency,
}: Props) {
  const [price, setPrice] = useState(initialPrice)
  const collectionStatistic = useCollectionStatistic()
  // The shortcuts and the "% above top offer" caption compare raw amounts, so
  // only use the top offer when it is denominated in the input's currency.
  // Otherwise a USDC top offer would prefill its amount into a SOL input.
  // Ids are chain-prefixed addresses whose casing varies by source, so compare
  // them case-insensitively as the currency store does.
  const collectionTopOffer = collectionStatistic?.topOffer
  const topOffer =
    collectionTopOffer?.currency.id.toLowerCase() === currency.id.toLowerCase()
      ? collectionTopOffer.amount
      : undefined

  const PriceShortcuts = useMemo(() => {
    if (!topOffer) return undefined

    return ({ setPrice }: { setPrice: (price: number) => void }) => (
      <Shortcuts topOffer={topOffer} setPrice={setPrice} />
    )
  }, [topOffer])

  useEffect(() => {
    onPriceChange(price)
  }, [price, onPriceChange])

  return (
    <PriceInput
      initialPrice={initialPrice}
      currency={currency}
      onPriceChange={setPrice}
      caption={<CompareToTopOfferLabel price={price} topOffer={topOffer} />}
      Shortcuts={PriceShortcuts}
      error={error}
    />
  )
}

const SHORTCUTS: { label: React.ReactNode; multiplier: number }[] = [
  {
    label: <Trans>Top offer</Trans>,
    multiplier: 1,
  },
  {
    label: <Trans>+1%</Trans>,
    multiplier: 1.01,
  },
  {
    label: <Trans>-1%</Trans>,
    multiplier: 0.99,
  },
  {
    label: <Trans>-5%</Trans>,
    multiplier: 0.95,
  },
]

function Shortcuts({
  topOffer,
  setPrice,
}: {
  topOffer: string
  setPrice: (price: number) => void
}) {
  return (
    <div className="flex items-center justify-between">
      {SHORTCUTS.map(({ label, multiplier }) => (
        <Button
          onClick={() => {
            setPrice(Big(topOffer).mul(multiplier).round(3).toNumber())
          }}
          variant="outline"
          type="button"
          key={multiplier}
        >
          {label}
        </Button>
      ))}
    </div>
  )
}

function CompareToTopOfferLabel({
  price,
  topOffer,
}: { price?: number; topOffer?: string }) {
  if (!price || !topOffer) return null

  const priceValue = Big(price)
  const topOfferValue = Big(topOffer)
  const percentageDifference = priceValue
    .minus(topOfferValue)
    .div(topOfferValue)
    .times(100)

  if (percentageDifference.lt(0)) {
    return (
      <Trans>
        Price is{" "}
        {percentageFormatter.format(percentageDifference.abs().toNumber())}%{" "}
        below the top offer
      </Trans>
    )
  }

  if (percentageDifference.gt(0)) {
    return (
      <Trans>
        Price is {percentageFormatter.format(percentageDifference.toNumber())}%{" "}
        above the top offer
      </Trans>
    )
  }

  return <Trans>Same as top offer</Trans>
}
